import classNames from "classnames";
import {observer} from "mobx-react-lite";
import React from "react";
import {Table as BootstrapTable} from "reactstrap";
import {SortOrder} from "../../../shared/enums/sortOrder";
import "./table.sass";

export interface ITableProps<T> {
  data: T[];
  columns: IColumnProps<T>[];
  rowClassFormatter?: (record: T) => string;
  keyFormatter: (record: T) => string;
  onSort: (sortField: string, sortOrder: SortOrder) => void;
  sortOrder?: SortOrder;
  disabled?: boolean;
}

export interface IColumnProps<T> {
  dataField: string;
  sort: boolean;
  formatter: (record: T) => React.ReactNode;
  headerFormatter?: (column: IColumnProps<T>) => React.ReactNode;
  text?: string;
  classes?: string;
}

const Table: React.FC<ITableProps<any>> = ({
  data,
  columns,
  rowClassFormatter,
  keyFormatter,
  onSort,
  sortOrder,
  disabled = false
}) => {
  const sortHandler = (column: IColumnProps<any>) => {
    // Don't sort if the column is not sortable
    if (!column.sort) return;

    const newSortOrder =
      sortOrder === SortOrder.Ascending
        ? SortOrder.Descending
        : SortOrder.Ascending;
    onSort?.(column.dataField, newSortOrder);
  };

  return (
    <BootstrapTable className={"data-table"} disabled={disabled}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.dataField}
              onClick={() => sortHandler(col)}
              className={classNames(col.dataField, {
                sortable: col.sort
              })}
            >
              {col.headerFormatter?.(col) ?? col.text}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data &&
          data.map((record) => (
            <tr
              className={rowClassFormatter?.(record)}
              key={keyFormatter(record)}
            >
              {columns.map((col) => (
                <td className={col.classes} key={col.dataField}>
                  {col.formatter(record)}
                </td>
              ))}
            </tr>
          ))}
      </tbody>
    </BootstrapTable>
  );
};

export default observer(Table);
